import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { Text, Stack, Inline, xcss } from '@atlaskit/primitives';
import { IconButton } from '@atlaskit/button/new';
import CopyIcon from '@atlaskit/icon/core/copy';
import Tooltip from '@atlaskit/tooltip';
import { ButtonItem } from '@atlaskit/menu';

import type { ContentVersionHistoryQuery_contentVersionHistory_nodes as VersionHistoryNodeType } from './graphql/__types__/ContentVersionHistoryQuery';
import { getFormattedDate } from './versionHistoryUtils';

const i18n = defineMessages({
	copyIconLabel: {
		id: 'version-history.copy.icon.label',
		defaultMessage: 'Copy icon',
		description: 'aria-label for copy icon',
	},
	copyIconTooltip: {
		id: 'version-history.copy.icon.tooltip',
		defaultMessage: 'Copy all content',
		description: 'tooltip text displayed on the copy icon before clicking',
	},
	copyIconTooltipCopied: {
		id: 'version-history.copy.icon.tooltip-copied',
		defaultMessage: 'Copied!',
		description: 'tooltip text displayed on the copy button after user has clicked',
	},
	lastEditedByLabel: {
		id: 'version-history.author.last-edited-by.label',
		defaultMessage: 'Last edited by {author}',
		description: 'Display text to show who edited a version of a whiteboard',
	},
	lastEditedByAnonymousUserLabel: {
		id: 'version-history.author.last-edited-by-anonymous-user.label',
		defaultMessage: 'Last edited by Anonymous',
		description: 'Display text to show an anonymous user edited a version of a whiteboard',
	},
	lastEditedByDeactivatedUserLabel: {
		id: 'version-history.author.last-edited-by-deactivated-user.label',
		defaultMessage: 'Last edited by {author} (Deactivated)',
		description: 'Display text to show a deactivated user edited a version of a whiteboard',
	},
});

const itemContainerStyles = xcss({
	paddingLeft: 'space.100',
});

type VersionHistoryNodeProps = {
	version: VersionHistoryNodeType;
	handleVersionClick: (version: VersionHistoryNodeType) => void;
	isSelected: boolean;
	isCopyAllowed: boolean;
	handleCopyClick: () => void;
};

export const VersionHistoryItem = ({
	version,
	handleVersionClick,
	isSelected,
	isCopyAllowed,
	handleCopyClick,
}: VersionHistoryNodeProps) => {
	const { formatMessage, formatDate, formatTime } = useIntl();
	const copyAllContentText = formatMessage(i18n.copyIconTooltip);
	const copiedText = formatMessage(i18n.copyIconTooltipCopied);
	const [isCopyClicked, setIsCopyClicked] = useState(false);

	const handleClick = useCallback(
		(e) => {
			e.stopPropagation();
			handleCopyClick();
			setIsCopyClicked(true);
		},
		[handleCopyClick],
	);

	const handleMouseLeave = () => {
		setIsCopyClicked(false);
	};

	const displayName = () => {
		const author = version?.author;

		if (!author?.id) {
			return <FormattedMessage {...i18n.lastEditedByAnonymousUserLabel} />;
		}

		if (author?.isActive) {
			return (
				<FormattedMessage {...i18n.lastEditedByLabel} values={{ author: author?.displayName }} />
			);
		}

		return (
			<FormattedMessage
				{...i18n.lastEditedByDeactivatedUserLabel}
				values={{ author: author?.displayName }}
			/>
		);
	};

	const shouldShowCopyIcon = isSelected && isCopyAllowed;

	return (
		<ButtonItem
			isSelected={isSelected}
			testId="version-history-item"
			onClick={() => handleVersionClick(version)}
		>
			<Inline alignBlock="center" spread="space-between" xcss={itemContainerStyles}>
				<Stack space="space.050">
					<Text size="medium">{getFormattedDate(version.updatedTime, formatDate, formatTime)}</Text>
					<Text size="small" color="color.text.subtle">
						{displayName()}
					</Text>
				</Stack>
				{shouldShowCopyIcon && (
					<Tooltip
						content={isCopyClicked ? copiedText : copyAllContentText}
						position="bottom"
						// force remount otherwise content is off-center
						key={isCopyClicked ? 'copied' : 'default'}
					>
						{(tooltipProps) => (
							<IconButton
								{...tooltipProps}
								appearance="subtle"
								icon={CopyIcon}
								label={formatMessage(i18n.copyIconLabel)}
								onClick={handleClick}
								onMouseLeave={handleMouseLeave}
								onBlur={handleMouseLeave}
								testId="version-content-copy-button"
							/>
						)}
					</Tooltip>
				)}
			</Inline>
		</ButtonItem>
	);
};
